import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ChatMarkdown from './chatMarkdown';
import ChatReportTable from './chatReportTable';

ChatMessage.propTypes = {
	message: PropTypes.object.isRequired,
	isSending: PropTypes.bool,
	onRetry: PropTypes.func,
};

export default function ChatMessage({ message, isSending, onRetry }) {
	const [copied, setCopied] = useState(false);
	const copiedTimer = useRef(null);

	useEffect(() => {
		return () => clearTimeout(copiedTimer.current);
	}, []);

	const copyAnswer = () => {
		// Clipboard access needs a secure context; no-op over plain http.
		if (!navigator?.clipboard?.writeText) {
			return;
		}
		navigator.clipboard.writeText(message.content).then(
			() => {
				setCopied(true);
				clearTimeout(copiedTimer.current);
				copiedTimer.current = setTimeout(() => setCopied(false), 2000);
			},
			() => {}
		);
	};

	if (message.isNotice) {
		return (
			<Box className="my-1 text-center text-[11px] italic text-gray-400">
				{message.content}
			</Box>
		);
	}

	if (message.role === 'user') {
		return (
			<Box className="flex justify-end">
				<Box
					className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md px-3 py-2 text-[13px] text-white"
					sx={{ backgroundColor: 'primary.main' }}>
					{message.content}
				</Box>
			</Box>
		);
	}

	return (
		<Box className="group flex flex-col items-start">
			<Box
				className={`max-w-[95%] rounded-2xl rounded-bl-md px-3 py-2 ${
					message.isError
						? 'bg-red-50 text-red-700'
						: 'bg-gray-100 text-gray-800'
				}`}>
				{message.isError ? (
					<Box className="whitespace-pre-wrap break-words text-[13px]">
						{message.content}
					</Box>
				) : (
					<ChatMarkdown content={message.content} />
				)}
			</Box>

			{message.report ? <ChatReportTable report={message.report} /> : null}

			<Box className="mt-0.5 flex items-center gap-1">
				{message.isError && message.prompt ? (
					<IconButton
						size="small"
						aria-label="Retry"
						disabled={isSending}
						onClick={() => onRetry(message.id, message.prompt)}>
						<RefreshIcon sx={{ fontSize: 16 }} />
					</IconButton>
				) : (
					<Tooltip title={copied ? 'Copied' : 'Copy'}>
						<IconButton
							size="small"
							aria-label="Copy answer"
							className="!opacity-0 transition-opacity group-hover:!opacity-100 focus:!opacity-100"
							onClick={copyAnswer}>
							{copied ? (
								<CheckIcon sx={{ fontSize: 16 }} color="primary" />
							) : (
								<ContentCopyIcon sx={{ fontSize: 16 }} />
							)}
						</IconButton>
					</Tooltip>
				)}
			</Box>
		</Box>
	);
}
